import { useState, useRef, useMemo, useCallback } from "react";
import type React from "react";
import type { Request } from "@/types/research.types";
import { useRequestGallery } from "./request-gallery/RequestGalleryContext";
import { useClassifiedRequests } from "@/hooks/useClassifiedRequests";
import { useDictation } from "@/hooks/useDictation";
import {
  handleCommandSelectLogic,
  createTextareaChangeHandler,
  createKeyPressHandler,
  createRequestSelectHandler,
  createSendMessageHandler,
} from "./chat-input.utils";

type MenuType = "slash" | "hash" | null;
type ChainProgressState = { current: number; total: number } | null;

export const useFilteredRequests = (
  activeMenu: MenuType,
  searchQuery: string,
  simpleRequests: Request[],
  chainedRequests: Request[]
) => {
  return useMemo(() => {
    const list = activeMenu === "slash" ? simpleRequests : chainedRequests;
    if (!activeMenu || !searchQuery) return list;
    return list.filter((req) => req.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [activeMenu, searchQuery, simpleRequests, chainedRequests]);
};

export const useDictationControls = (
  onTranscript: React.Dispatch<React.SetStateAction<string>>
) => {
  return useDictation({ onTranscript });
};

export const useChatInputState = (requestType: "discuss" | "research") => {
  const [chatMessage, setChatMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { openGallery } = useRequestGallery();
  const [galleryId] = useState(() => Math.random().toString(36).slice(2, 11));
  const [activeMenu, setActiveMenu] = useState<MenuType>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { simpleRequests, chainedRequests, requestMap } = useClassifiedRequests(requestType);
  const [chainProgress, setChainProgress] = useState<ChainProgressState>(null);
  const currentRequests = useFilteredRequests(
    activeMenu,
    searchQuery,
    simpleRequests,
    chainedRequests
  );
  return {
    chatMessage,
    setChatMessage,
    textareaRef,
    openGallery,
    galleryId,
    activeMenu,
    setActiveMenu,
    searchQuery,
    setSearchQuery,
    selectedIndex,
    setSelectedIndex,
    requestMap,
    chainProgress,
    setChainProgress,
    currentRequests,
  };
};

export interface ChatInputState {
  chatMessage: string;
  setChatMessage: React.Dispatch<React.SetStateAction<string>>;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  activeMenu: MenuType;
  setActiveMenu: React.Dispatch<React.SetStateAction<MenuType>>;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
  requestMap: Record<string, Request>;
  chainProgress: ChainProgressState;
  setChainProgress: React.Dispatch<React.SetStateAction<ChainProgressState>>;
  currentRequests: Request[];
}

export function useRequestCallbacks(
  state: ChatInputState,
  onSendMessage: (message: string) => Promise<void> | void,
  setPendingChainRequest: React.Dispatch<React.SetStateAction<Request | null>>
) {
  const handleRequestSelect = useCallback(
    createRequestSelectHandler(
      state.requestMap,
      onSendMessage,
      state.setChainProgress,
      state.setChatMessage,
      state.textareaRef
    ),
    [
      state.requestMap,
      onSendMessage,
      state.setChainProgress,
      state.setChatMessage,
      state.textareaRef,
    ]
  );
  const handleCommandSelect = useCallback(
    (c: string, request?: Request) =>
      handleCommandSelectLogic(
        c,
        state.activeMenu,
        state.searchQuery,
        state.chatMessage,
        onSendMessage,
        state.setChatMessage,
        state.setActiveMenu,
        state.setSearchQuery,
        state.textareaRef,
        request,
        (req: Request) => setPendingChainRequest(req)
      ),
    [
      state.activeMenu,
      state.searchQuery,
      state.chatMessage,
      onSendMessage,
      state.setChatMessage,
      state.setActiveMenu,
      state.setSearchQuery,
      state.textareaRef,
    ]
  );
  return { handleRequestSelect, handleCommandSelect };
}

export function useFormCallbacks(
  state: ChatInputState,
  onSendMessage: (message: string) => Promise<void> | void,
  handleCommandSelect: (c: string, request?: Request) => void
) {
  const { currentRequests } = state;
  const handleTextareaChange = useCallback(
    createTextareaChangeHandler(
      state.setChatMessage,
      state.setActiveMenu,
      state.setSearchQuery,
      state.setSelectedIndex
    ),
    [state.setChatMessage, state.setActiveMenu, state.setSearchQuery, state.setSelectedIndex]
  );
  const handleSendMessage = useCallback(
    createSendMessageHandler(
      state.chatMessage,
      state.textareaRef,
      state.setChatMessage,
      onSendMessage
    ),
    [state.chatMessage, state.textareaRef, state.setChatMessage, onSendMessage]
  );
  const handleKeyPress = useCallback(
    createKeyPressHandler(
      state.activeMenu,
      currentRequests,
      state.selectedIndex,
      state.setSelectedIndex,
      state.setActiveMenu,
      handleCommandSelect,
      handleSendMessage,
      state.textareaRef
    ),
    [
      state.activeMenu,
      currentRequests,
      state.selectedIndex,
      state.setSelectedIndex,
      state.setActiveMenu,
      handleCommandSelect,
      handleSendMessage,
      state.textareaRef,
    ]
  );
  return { handleTextareaChange, handleSendMessage, handleKeyPress };
}
